import { useDispatch } from 'react-redux'
import {
  setProgress,
  setOpen,
  setFilename,
  setRemainingTime,
  setStatus,
} from '../redux/uploadSlice'
import axios from 'axios'
import { useAppSelector } from '@/redux/hooks'
import { Message } from '@/components/message'

const useUploadProgress = () => {
  const dispatch = useDispatch()
  const progress = useAppSelector(state => state.upload.progress)
  const open = useAppSelector(state => state.upload.open)
  const filename = useAppSelector(state => state.upload.filename)
  const remainingTime = useAppSelector(state => state.upload.remainingTime)
  const status = useAppSelector(state => state.upload.status)

  const setWindowVariable = (key, value) => {
    if (typeof window !== 'undefined') {
      window[key] = value
    } else {
      console.error('window 对象未定义，可能在非浏览器环境中运行')
    }
  }

  const startUpload = (file: File, startDate: number, uploadUrl: string, id: string) => {
    const chunkSize = 1024 * 1024 * 5
    const totalChunks = Math.ceil(file.size / chunkSize)
    console.log('file', file)

    dispatch(setStatus('uploading'))
    dispatch(setFilename(file.name))

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // 这里可以执行一些操作
      console.log('页面即将刷新或关闭')
      // 如果需要阻止默认行为，可以取消事件
      event.preventDefault()
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    const removeEventListener = () => window.removeEventListener('beforeunload', handleBeforeUnload)

    if (file.size <= chunkSize) {
      // 如果文件大小小于等于5MB，直接上传整个文件
      const formData = new FormData()
      formData.append('file', file)
      console.log('开始上传文件:', file.name)

      axios({
        url: `${process.env.NODE_ENV === 'dev' ? '/api' : ''}${uploadUrl}`,
        method: 'POST',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
          Token: JSON.parse(localStorage.getItem('token') as string)?.token,
          'User-ID': JSON.parse(localStorage.getItem('token') as string)?.id,
        },
        params: { id, type: 3 },
        onUploadProgress: progressEvent => {
          console.log('progressEvent', progressEvent)
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          dispatch(setProgress(percentCompleted))

          if (startDate !== null) {
            const currentTime = Date.now()
            const elapsedTime = (currentTime - startDate) / 1000 // 转换为秒
            const uploadSpeed = progressEvent.loaded / elapsedTime // 每秒上传的字节数
            const remainingBytes = progressEvent.total - progressEvent.loaded
            const estimatedRemainingTime =
              remainingBytes / uploadSpeed / 60 < 1 && remainingBytes > 0
                ? 1
                : remainingBytes / uploadSpeed / 60
            // 估算剩余时间（单位：分钟）
            dispatch(setRemainingTime(estimatedRemainingTime))
          }
        },
      })
        .then(response => {
          console.log('文件上传成功:', response.data)
          const { code } = response.data
          removeEventListener()
          if (code === 200) {
            dispatch(setStatus('success'))
          } else {
            dispatch(setStatus('failed'))
            Message.error(response.data.msg)
          }
        })
        .catch(error => {
          console.error('文件上传失败:', error)
          dispatch(setStatus('failed'))
          removeEventListener()
        })
    } else {
      console.log('大于5M')
      // 如果文件大小大于5MB，分片上传
      let currentChunk = 0
      let uploadedBytes = 0

      const uploadChunk = (chunk: Blob) => {
        console.log('window', window.uploadStatus)
        const formData = new FormData()
        formData.append('file', chunk)

        let type = 4
        if (currentChunk === 0) {
          type = 1 // 第一个分片时，type为1
        } else if (currentChunk === totalChunks - 1) {
          type = 2 // 最后一个分片时，type为2
        }

        axios({
          url: `${process.env.NODE_ENV === 'dev' ? '/api' : ''}${uploadUrl}`, // 替换为实际的上传URL
          method: 'POST',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
            Token: JSON.parse(localStorage.getItem('token') as string)?.token,
            'User-ID': JSON.parse(localStorage.getItem('token') as string)?.id,
          },
          params: { id, type },
        })
          .then(response => {
            console.log('分片上传成功:', response.data)
            const { code } = response.data
            if (code === 200) {
              currentChunk++
              // 更新已上传字节数
              uploadedBytes += chunk.size
              dispatch(setProgress(Math.round((uploadedBytes * 100) / file.size)))
              console.log('uploadedBytes', uploadedBytes)

              if (startDate !== null) {
                const currentTime = Date.now()
                const elapsedTime = (currentTime - startDate) / 1000 // 转换为秒
                const uploadSpeed = uploadedBytes / elapsedTime // 每秒上传的字节数
                const remainingBytes = file.size - uploadedBytes
                const estimatedRemainingTime = Math.ceil(
                  remainingBytes / uploadSpeed / 60 < 1 && remainingBytes > 0
                    ? 1
                    : remainingBytes / uploadSpeed / 60,
                )
                // 估算剩余时间（单位：分钟）
                dispatch(setRemainingTime(estimatedRemainingTime))
              }

              if (currentChunk < totalChunks) {
                const nextChunk = file.slice(
                  currentChunk * chunkSize,
                  (currentChunk + 1) * chunkSize,
                )
                if (window.uploadStatus === 'waiting') return
                uploadChunk(nextChunk)
              } else {
                removeEventListener()
                dispatch(setStatus('success'))
              }
            } else {
              removeEventListener()
              dispatch(setStatus('failed'))
              Message.error(response.data.msg)
            }
          })
          .catch(error => {
            console.error('分片上传失败:', error)
            dispatch(setStatus('failed'))
            dispatch(setProgress(0))
            removeEventListener()
          })
      }

      const firstChunk = file.slice(0, chunkSize)
      uploadChunk(firstChunk)
    }
  }

  const closeProgress = () => {
    dispatch(setRemainingTime(0))
    dispatch(setFilename(''))
    dispatch(setStatus('waiting'))
    dispatch(setProgress(0))
  }

  const closeCard = () => {
    dispatch(setOpen(false))
    dispatch(setProgress(0))
  }

  return {
    progress, // 上传进度
    status, // 上传状态
    open, // 是否显示浮框
    filename, // 上传的文件名
    remainingTime, // 剩余时间（单位：分钟）
    startUpload, // 开始上传
    closeProgress, // 关闭进度条
    closeCard, // 关闭浮框
    setWindowVariable, // 设置 window 变量
  }
}

export default useUploadProgress
